import React, { FC } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { handleWithdraw } from '../../helpers/handleWithdraw';
import { findAgreement } from '../../helpers/findAgreement';

import { UserSupply } from '@/shared/store/supplies';
import { ROLE } from '@/shared/api/borrow';
import { AssetWithIcon, Button, ButtonSize, ButtonVariant, Skeleton } from '@/shared/ui';
import { useFetchAgreements } from '@/shared/hooks/useFetchAgreements';
import { formatSmart, formatWithMaxPrecision } from '@/shared/utils/helpers';

type UserSuppliesRowsProps = {
    items: UserSupply[];
};

export const UserSuppliesRows: FC<UserSuppliesRowsProps> = ({ items }) => {
    const navigate = useNavigate();
    const { agreements, refresh } = useFetchAgreements(ROLE.LENDER);
    const dispatch = useDispatch();

    if (!items) {
        return null;
    }

    return (
        <>
            {items.map((item, index) => {
                const agreement = item.agreementAddress
                    ? findAgreement(agreements, ROLE.LENDER, item.agreementAddress)
                    : null;
                const handleWithdrawFn = agreement
                    ? handleWithdraw(dispatch, item, agreement, refresh)
                    : undefined;
                const handleDetails = () => {
                    navigate(`/pool/${item.agreementAddress}?role=lender`);
                };

                const withdrawalBalance = formatSmart(
                    formatWithMaxPrecision(item.balance || '', item.tokenDecimals),
                );

                const withdrawalReward = formatSmart(item.reward!);

                return (
                    <tr
                        key={index}
                        className='h-[70px] border-t border-opaque [&_td]:font-semibold'
                        data-testid={`supply-row-${item.agreementTitle}`}
                    >
                        <td className='pl-6' data-testid={`supply-withdrawal-title`}>
                            {item.agreementTitle}
                        </td>
                        <td>
                            <span data-testid={`supply-withdrawal-asset`}>
                                <AssetWithIcon
                                    assetName={item.tokenSymbol || 'DAI'}
                                    className='[&_img]:h-[24px] [&_img]:w-[24px] [&_div]:text-[14px]'
                                />
                            </span>
                        </td>
                        <td>
                            <span data-testid={`supply-withdrawal-apy`}>{item.apy}</span>
                            <span>%</span>
                        </td>
                        <td className='pr-4'>
                            {withdrawalBalance !== 'NaN' ? (
                                <span data-testid={`supply-withdrawal-balance`}>
                                    {withdrawalBalance} <span>{item.tokenSymbol}</span>
                                </span>
                            ) : (
                                <span data-testid={`supply-withdrawal-balance`}>
                                    <Skeleton />
                                </span>
                            )}
                        </td>
                        <td className='pr-4'>
                            <div className='flex items-center [&>svg]:w-8 [&>svg]:h-8 [&>svg]:mr-4 w-[130px]'>
                                {withdrawalReward !== 'NaN' ? (
                                    <div data-testid={`supply-withdrawal-reward`}>
                                        {withdrawalReward}{' '}
                                        <span data-testid={`supply-withdrawal-reward-symbol`}>
                                            {item.tokenSymbol}
                                        </span>
                                    </div>
                                ) : (
                                    <div data-testid={`supply-withdrawal-reward`}>
                                        <span
                                            className='blur'
                                            data-testid={`supply-withdrawal-reward-symbol`}
                                        >
                                            Not Available
                                        </span>
                                    </div>
                                )}
                            </div>
                        </td>
                        <td className='w-[10%] pr-6'>
                            <div className='flex justify-end gap-5'>
                                <Button
                                    variant={ButtonVariant.Primary}
                                    size={ButtonSize.Medium}
                                    name={`supply-withdraw-${index}`}
                                    className='w-[100px] ml-6 mr-auto flex justify-center items-center border-none'
                                    onClick={handleWithdrawFn}
                                >
                                    Withdraw
                                </Button>
                                <Button
                                    variant={ButtonVariant.Secondary}
                                    size={ButtonSize.Medium}
                                    className='w-24'
                                    onClick={handleDetails}
                                >
                                    Details
                                </Button>
                            </div>
                        </td>
                    </tr>
                );
            })}
        </>
    );
};
